import { useState, useEffect } from 'react';
import { Box, Collapse, Alert, AlertTitle } from '@mui/material';
import { ErrorMessage } from '../../ErrorMessage';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, AppState, AppStore } from '../../../store';
import { GenericSimpleForm } from '../../../components/Layouts/GenericSimpleForm';
import {
  ApplicationFormFields,
  EAction,
  EActionType,
  EDataTableType,
  ELovValues,
} from '../../../data/Constants';
import { useTranslation } from 'react-i18next';
import { getEnumOptions } from '../../../services/DataHelperFunctions';
import {
  createApplication,
  getApplicationDetails,
  getApplications,
  updateApplication,
} from '../../../store/states/ApplicationSlice';
import { setAlertMessage } from '../../../store/states/AlertMessageSlice';
import { resetDialogMessage } from '../../../store/states/DialogSlice';
import { resetError } from '../../../store/states/ErrorSlice';
import { TFunction } from 'i18next';

import type { IAPIResponse, IApplication, IError, IManipulationProps } from '../../../../types';

//To call create and update apis
function callCreateOrUpdateApi(
  manipulationType: string,
  formData: IApplication,
  t: TFunction,
  dispatch: AppDispatch
): void {
  if (manipulationType === EActionType.Edit) {
    dispatch(
      updateApplication({
        request: {
          ...formData,
          name: formData.name.trimEnd(),
          description: formData.description.trim(),
        },
      })
    ).then((res) => {
      handleApiResponse(
        res.payload as IAPIResponse,
        t('messages.fail.update'),
        t('messages.success.update'),
        dispatch
      );
    });
  } else {
    dispatch(
      createApplication({
        request: {
          ...formData,
          name: formData.name.trimEnd(),
          description: formData.description.trim(),
        },
      })
    ).then((res) => {
      handleApiResponse(
        res.payload as IAPIResponse,
        t('messages.fail.create'),
        t('messages.success.create'),
        dispatch
      );
    });
  }
}

function handleApiResponse(
  res: IAPIResponse,
  failMessage: string,
  successMessage: string,
  dispatch: AppDispatch
) {
  const applications = AppStore.getState().administration.applications;
  if (res?.data) {
    dispatch(
      getApplications({
        page: applications.currentPage,
        limit: applications.recordsPerPage,
        searchCode: applications.searchKey,
      })
    );
    dispatch(setAlertMessage({ show: true, message: successMessage, type: 'success' }));
    dispatch(resetDialogMessage());
  } else {
    const message = res?.error?.message ? res.error.message : failMessage;
    dispatch(setAlertMessage({ show: true, message: message, type: 'error' }));
  }
}

const loadFormData = async (
  props: IManipulationProps,
  setInitialApplicationData: (value: React.SetStateAction<IApplication>) => void,
  dispatch: AppDispatch
): Promise<void> => {
  let applicationDetails = await dispatch(getApplicationDetails({ request: [props.id] })).unwrap();

  if ('data' in applicationDetails && applicationDetails.data) {
    setInitialApplicationData(applicationDetails.data[0]);
  }
};

const handleError = (
  error: IError,
  setDisplayAlertMessage: (value: React.SetStateAction<boolean>) => void
): void => {
  if (error.code && (error.action === EAction.Create || error.action === EAction.Update)) {
    setDisplayAlertMessage(true);
  }
};

const updateFormFieldValues = (fieldName: string, values: any[]) => {
  const field = ApplicationFormFields.find((app) => app.field === fieldName);
  if (field) {
    field.values = values;
  }
};

export const ApplicationManipulationForm = (props: IManipulationProps) => {
  const manipulationType = props.type; //To get the type of manipulation being performed
  const { t } = useTranslation();
  const [displayAlertMessage, setDisplayAlertMessage] = useState<boolean>(false);
  const enumValues = useSelector((state: AppState) => state.dataMaintainance.enumListOfValues);
  const channels = getEnumOptions(enumValues, ELovValues.ECHT);
  const views = getEnumOptions(enumValues, ELovValues.EVIEW);
  const error = useSelector((state: AppState) => state.error);
  const dispatch = useDispatch<AppDispatch>();
  const initialData: IApplication = {
    name: '',
    description: '',
    channel: '',
    views: null,
    isActive: true,
  };

  const [initialFormData, setInitialFormData] = useState<IApplication>(initialData);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
  };

  const onSubmit = (data: IApplication) => {
    dispatch(resetError());
    setDisplayAlertMessage(false);
    callCreateOrUpdateApi(manipulationType, data, t, dispatch);
  };

  useEffect(() => {
    updateFormFieldValues('channel', channels);
  }, [channels]);

  useEffect(() => {
    updateFormFieldValues('views', views);
  }, [views]);

  useEffect(() => {
    if (manipulationType === EActionType.Edit) {
      loadFormData(props, setInitialFormData, dispatch);
    }
  }, []);

  useEffect(() => {
    handleError(error, setDisplayAlertMessage);
  }, [error]);

  return (
    <Box className="manipulationBox">
      <Box className="manipulationForm">
        <Collapse in={displayAlertMessage} className="show-alert">
          <Alert className="errorMessage" severity="error" onClose={handleClose}>
            <AlertTitle>
              <ErrorMessage error={error} manipulationType={manipulationType} />
            </AlertTitle>
          </Alert>
        </Collapse>
        <GenericSimpleForm
          formFields={ApplicationFormFields}
          initialData={initialFormData}
          onSubmit={onSubmit}
          manipulationType={manipulationType}
          setDisplayAlertMessage={setDisplayAlertMessage}
          type={EDataTableType.Applications}
          uniqueKey="name"
        />
      </Box>
    </Box>
  );
};
